import {
  ErrorMessage,
  Modal,
  UPDATE_CURRENT_COMPANY,
  useFocus,
} from '@employ-engage/common-components';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { NextLevelKey, randomRgba } from '@employ-engage/data';
import kebabCase from 'lodash.kebabcase';
import toLower from 'lodash.tolower';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';

export interface AddOrgLevelModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  nextLevelKeys: NextLevelKey[];
  refetchCompany: () => void;
}

export function AddOrgLevelModal({
  open,
  setOpen,
  nextLevelKeys,
  refetchCompany,
}: AddOrgLevelModalProps) {
  const { t } = useTranslation(['common']);
  const focusRef = useFocus();
  const [updateCompany] = useMutation(UPDATE_CURRENT_COMPANY);
  const { handleSubmit, values, errors, touched, setFieldValue, resetForm } =
    useFormik({
      initialValues: {
        id: '',
        name: '',
        color: randomRgba(),
      },
      onSubmit: (values, { resetForm }) => {
        updateCompany({
          variables: {
            input: {
              orgTreeNextLevelKeys: [
                ...nextLevelKeys.map((key) => {
                  return {
                    id: key.id,
                    name: key.name,
                    color: key.color,
                  };
                }),
                values,
              ],
            },
          },
        })
          .then(() => {
            resetForm();
            refetchCompany();
            setOpen(false);
          })
          .catch((error) => {
            console.error('Error updating document: ', error);
          });
      },
      validationSchema: Yup.object({
        id: Yup.string().notOneOf(
          nextLevelKeys.map((key) => key.id),
          t('ID already exists')
        ),
        name: Yup.string()
          .required('Required')
          .notOneOf(
            nextLevelKeys.map((key) => key.name),
            t('Name already exists')
          ),
      }),
    });

  useEffect(() => {
    resetForm();
  }, [open]);

  return (
    <Modal panelClassName="w-full max-w-[500px]" open={open} setOpen={setOpen}>
      <form onSubmit={handleSubmit}>
        <div>
          <label
            htmlFor="name"
            className="org-level-name-input-label block text-sm font-medium text-light-primaryText dark:text-dark-primaryText"
          >
            {t('Name')}
          </label>
          <div className="flex items-center">
            <input
              ref={focusRef}
              type="text"
              name="name"
              id="name"
              placeholder={t('e.g. Company, Department, Team, etc.')}
              className="org-level-name-input mr-5 mt-1 text-light-primaryText dark:text-light-primaryText focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
              onChange={(e) => {
                setFieldValue('id', kebabCase(toLower(e.target.value)));
                setFieldValue('name', e.target.value);
                setFieldValue('color', randomRgba());
              }}
              value={values.name}
            />
          </div>
          <ErrorMessage errors={errors.name} touched={touched.name} />
        </div>
        <div className="mt-4">
          <button
            type="submit"
            className="save-org-level-button rounded-md bg-indigo-500 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500"
          >
            {t('Save Organization Level')}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default AddOrgLevelModal;
